// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Reusable "Things to check" checklist card (FR-14). Checkbox state and the
// "Add as checklist" action are LOCAL UI ONLY — nothing is written to the
// encounter or any clinical record.

import { useState } from 'react';
import { ELIA_COLORS, ELIA_FONT } from './elia-theme';
import { EliaCheckIcon } from './EliaIcons';
import type { EliaMessageCard as EliaMessageCardModel } from './elia-chat-types';

export function EliaMessageCard({ card }: { card: EliaMessageCardModel }) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [locallyAdded, setLocallyAdded] = useState(false);

  return (
    <div
      style={{
        marginTop: 8,
        background: ELIA_COLORS.surface,
        border: `1px solid ${ELIA_COLORS.border}`,
        borderRadius: 16,
        boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px -1px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        fontFamily: ELIA_FONT,
      }}
    >
      <div
        style={{
          padding: '12px 16px 9px',
          borderBottom: `1px solid ${ELIA_COLORS.borderSubtle}`,
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 600,
            color: ELIA_COLORS.text,
          }}
        >
          {card.title}
        </p>
      </div>

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: '8px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {card.items.map((item) => {
          const isChecked = !!checked[item.id];
          return (
            <li key={item.id} style={{ margin: 0 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 12,
                  color: ELIA_COLORS.textSecondary,
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => setChecked((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: 1,
                    height: 1,
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    width: 14,
                    height: 14,
                    flexShrink: 0,
                    borderRadius: 4,
                    border: `1px solid ${
                      isChecked ? ELIA_COLORS.pink : ELIA_COLORS.checkboxBorder
                    }`,
                    background: isChecked ? ELIA_COLORS.pink : 'transparent',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isChecked ? <EliaCheckIcon size={10} color="#ffffff" /> : null}
                </span>
                {item.label}
              </label>
            </li>
          );
        })}
      </ul>

      {card.informationUsed ? (
        <div
          style={{
            padding: '9px 16px 8px',
            borderTop: `1px solid ${ELIA_COLORS.borderSubtle}`,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 10,
              lineHeight: '15px',
              color: ELIA_COLORS.textFaint,
            }}
          >
            {card.informationUsed}
          </p>
        </div>
      ) : null}

      {card.actionLabel ? (
        <div style={{ padding: '6px 16px 10px' }}>
          <button
            type="button"
            // Local-only: must never imply the checklist was saved (FR-14).
            title="Kept on this screen only — not saved to the patient record"
            onClick={() => setLocallyAdded(true)}
            style={{
              minHeight: 24,
              display: 'inline-flex',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
              padding: '4px 0',
              cursor: 'pointer',
              color: ELIA_COLORS.pink,
              fontFamily: ELIA_FONT,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {card.actionLabel}
          </button>
          {locallyAdded ? (
            <p
              role="status"
              style={{
                margin: '4px 0 0',
                fontSize: 10,
                lineHeight: '15px',
                color: ELIA_COLORS.textFaint,
              }}
            >
              Kept on this screen only — not saved to the patient record.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
